import { createElement, X } from "lucide";
import {
  isSafeSupportAttachmentFileName,
  isSafeSupportTicketId,
  isSupportAttachmentMimeType,
  maxSupportAttachmentBytes,
  maxSupportAttachmentsPerMessage,
} from "@shdeed/foundation";
import "./supportAttachments.css";

export type SupportImageScope = {
  kind: "member" | "platform";
  organizationId: string;
  ticketId: string;
};
type Post = (name: string, body: Record<string, unknown>) => Promise<Record<string, unknown>>;
type Context = { post: Post; isCurrent: () => boolean; locale: string };
type Attachment = { fileName: string; storagePath: string; mimeType: string; sizeBytes: number };

export function supportImageRequest(
  storagePath: string,
  organizationId: string,
): SupportImageScope & { fileName: string } {
  const member =
    /^organizations\/([^/]+)\/support\/memberTickets\/([^/]+)\/attachments\/([^/]+)$/u.exec(
      storagePath,
    );
  const platform = /^platformSupportTickets\/([^/]+)\/attachments\/([^/]+)$/u.exec(storagePath);
  const kind = member ? "member" : "platform";
  const ticketId = member?.[2] ?? platform?.[1];
  const fileName = member?.[3] ?? platform?.[2];
  if (
    (member && member[1] !== organizationId) ||
    !organizationId ||
    !isSafeSupportTicketId(ticketId) ||
    !isSafeSupportAttachmentFileName(fileName)
  )
    throw new Error("invalid_attachment");
  return { kind, organizationId, ticketId, fileName };
}

export function decodeSupportImage(body: Record<string, unknown>): Blob {
  if (
    !isSupportAttachmentMimeType(body.mimeType) ||
    typeof body.dataBase64 !== "string" ||
    body.dataBase64.length > 4 * Math.ceil(maxSupportAttachmentBytes / 3) ||
    typeof body.sizeBytes !== "number" ||
    body.sizeBytes <= 0 ||
    body.sizeBytes > maxSupportAttachmentBytes
  )
    throw new Error("invalid_attachment");
  const raw = atob(body.dataBase64);
  if (raw.length !== body.sizeBytes) throw new Error("invalid_attachment");
  return new Blob([Uint8Array.from(raw, (char) => char.charCodeAt(0))], { type: body.mimeType });
}

export async function uploadSupportImages(
  files: File[],
  scope: SupportImageScope,
  context: Context,
): Promise<Attachment[]> {
  const l = (en: string, ar: string) => (context.locale === "ar" ? ar : en);
  if (files.length > maxSupportAttachmentsPerMessage)
    throw new Error(l("Choose up to 5 images.", "اختر 5 صور كحد أقصى."));
  const result: Attachment[] = [];
  for (const file of files) {
    if (!context.isCurrent()) throw new Error("support_context_changed");
    if (
      !file.size ||
      file.size > maxSupportAttachmentBytes ||
      !isSupportAttachmentMimeType(file.type)
    )
      throw new Error(
        l(
          "Choose JPEG, PNG, WebP, HEIC, or HEIF images, up to 10 MB each.",
          "اختر صور JPEG أو PNG أو WebP أو HEIC أو HEIF بحجم لا يتجاوز 10 ميغابايت للصورة.",
        ),
      );
    const bytes = new Uint8Array(await file.arrayBuffer());
    let raw = "";
    for (let i = 0; i < bytes.length; i += 8192)
      raw += String.fromCharCode(...bytes.subarray(i, i + 8192));
    if (!context.isCurrent()) throw new Error("support_context_changed");
    const response = await context.post("uploadSupportAttachment", {
      ...scope,
      mimeType: file.type,
      dataBase64: btoa(raw),
    });
    if (!context.isCurrent()) throw new Error("support_context_changed");
    if (
      typeof response.storagePath !== "string" ||
      response.mimeType !== file.type ||
      response.sizeBytes !== file.size ||
      !isSafeSupportAttachmentFileName(response.fileName)
    )
      throw new Error("invalid_attachment");
    const parsed = supportImageRequest(response.storagePath, scope.organizationId);
    if (
      parsed.kind !== scope.kind ||
      parsed.ticketId !== scope.ticketId ||
      parsed.fileName !== response.fileName
    )
      throw new Error("invalid_attachment");
    result.push({
      fileName: response.fileName,
      storagePath: response.storagePath,
      mimeType: file.type,
      sizeBytes: file.size,
    });
  }
  return result;
}

export function openSupportImage(
  storagePath: string,
  organizationId: string,
  context: Context,
): void {
  const l = (en: string, ar: string) => (context.locale === "ar" ? ar : en);
  const dialog = document.createElement("dialog");
  dialog.className = "support-image-dialog";
  dialog.dir = context.locale === "ar" ? "rtl" : "ltr";
  dialog.setAttribute("aria-label", l("Support image", "صورة الدعم"));
  const header = document.createElement("header");
  const title = document.createElement("h2");
  title.textContent = l("Support image", "صورة الدعم");
  const close = document.createElement("button");
  close.type = "button";
  close.title = l("Close", "إغلاق");
  close.setAttribute("aria-label", close.title);
  close.append(createElement(X, { "aria-hidden": "true" }));
  header.append(title, close);
  const content = document.createElement("div");
  content.className = "support-image-content";
  content.setAttribute("aria-live", "polite");
  dialog.append(header, content);
  let closed = false;
  let objectUrl: string | undefined;
  const dispose = () => {
    if (closed) return;
    closed = true;
    clearInterval(watch);
    window.removeEventListener("pagehide", dispose);
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    content.replaceChildren();
    dialog.close();
    dialog.remove();
  };
  const watch = setInterval(() => {
    if (!context.isCurrent()) dispose();
  }, 250);
  window.addEventListener("pagehide", dispose);
  close.addEventListener("click", dispose);
  dialog.addEventListener("close", dispose);
  dialog.addEventListener("cancel", dispose);
  const load = async () => {
    content.textContent = l("Loading image...", "جار تحميل الصورة...");
    try {
      if (!context.isCurrent()) return dispose();
      const result = await context.post(
        "readSupportAttachment",
        supportImageRequest(storagePath, organizationId),
      );
      if (closed || !context.isCurrent()) return dispose();
      const blob = decodeSupportImage(result);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = URL.createObjectURL(blob);
      const img = document.createElement("img");
      img.alt = l("Support attachment", "مرفق الدعم");
      img.onload = () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        objectUrl = undefined;
      };
      img.onerror = () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        objectUrl = undefined;
        content.textContent = l(
          "This image format cannot be displayed in this browser.",
          "لا يمكن عرض تنسيق هذه الصورة في هذا المتصفح.",
        );
      };
      img.src = objectUrl;
      content.replaceChildren(img);
    } catch {
      if (closed || !context.isCurrent()) return dispose();
      content.textContent = l(
        "Image unavailable. Check your connection and access, then retry.",
        "الصورة غير متاحة. تحقق من الاتصال وصلاحية الوصول ثم أعد المحاولة.",
      );
      const retry = document.createElement("button");
      retry.textContent = l("Retry", "إعادة المحاولة");
      retry.addEventListener("click", () => void load());
      content.append(retry);
    }
  };
  document.body.append(dialog);
  dialog.showModal();
  void load();
}
